import React, {FC, useEffect, useState} from "react"
import {
    Alert,
    Button,
    ButtonGroup,
    Card,
    Col,
    Container,
    Form,
    Modal,
    OverlayTrigger,
    Row,
    Tooltip,
    Spinner,
} from "react-bootstrap"
import axios from "axios"
import moment from "moment"
import BootstrapTable from "react-bootstrap-table-next"
import filterFactory, {
    Comparator,
    selectFilter,
    textFilter,
} from "react-bootstrap-table2-filter"
import {useNavigate, useParams} from "react-router-dom"

import {useAuth} from "../../context/AuthProvider"
import {Trans, useTranslation} from "react-i18next"
import {
    ManagedExam,
    ParticipantData,
    ParticipantEntry,
} from "./participant.types"
import {
    getParticipantStatus,
    getParticipantStatusFilterValue,
    getParticipantStatusLabel,
} from "./participantStatus"

type ParticipantViewProps = Record<string, unknown>

const emptyParticipantData: ParticipantData = {
    entries: [],
    legitimizationCount: 0,
    bookingCount: 0,
    attended: 0,
    notAttended: 0,
    handicapCount: 0,
    isDemo: false,
}

const formatEsi = (esi: string | undefined): string => {
    if (!esi) return "–"

    return esi.replace(
        "urn:schac:personalUniqueCode:int:esi:tu-darmstadt.de:",
        ""
    )
}

const ParticipantView: FC<ParticipantViewProps> = () => {
    const auth = useAuth()
    const nav = useNavigate()
    const params = useParams()
    const {t, i18n} = useTranslation("examManagement")

    const examId = params.examId

    const [exam, setExam] = useState<ManagedExam | null>(null)
    const [data, setData] = useState<ParticipantData>(emptyParticipantData)
    const [loading, setLoading] = useState<boolean>(true)
    const [loadError, setLoadError] = useState<boolean>(false)
    const [selectedRows, setSelectedRows] = useState<ParticipantEntry[]>([])
    const [expandedRow, setExpandedRow] = useState<string | null>(null)
    const [handicapEntry, setHandicapEntry] = useState<ParticipantEntry | null>(null)
    const [handicapForm, setHandicapForm] = useState({
        handicap: 0,
        exclusiveBooking: false,
        handicapNotes: "",
        notifyParticipant: false,
    })
    const [savingHandicap, setSavingHandicap] = useState(false)
    const [handicapError, setHandicapError] = useState<string | null>(null)
    const [showManualParticipants, setShowManualParticipants] = useState(false)
    const [manualIdentifiers, setManualIdentifiers] = useState("")
    const [manualNotifyParticipants, setManualNotifyParticipants] = useState(false)
    const [savingManualParticipants, setSavingManualParticipants] = useState(false)
    const [manualParticipantsError, setManualParticipantsError] = useState<string | null>(null)
    const [manualParticipantsResult, setManualParticipantsResult] = useState<string | null>(null)
    const [showFixedDateAttendance, setShowFixedDateAttendance] = useState(false)
    const [fixedDateIdentifiers, setFixedDateIdentifiers] = useState("")
    const [savingFixedDateAttendance, setSavingFixedDateAttendance] = useState(false)
    const [fixedDateAttendanceError, setFixedDateAttendanceError] = useState<string | null>(null)
    const [fixedDateAttendanceResult, setFixedDateAttendanceResult] = useState<string | null>(null)
    const [participantActionError, setParticipantActionError] = useState<string | null>(null)
    const [participantActionSuccess, setParticipantActionSuccess] = useState<string | null>(null)
    const [pendingManualDeletion, setPendingManualDeletion] = useState<ParticipantEntry[]>([])
    const [deletingManualParticipants, setDeletingManualParticipants] = useState(false)
    const mayManageHandicap =
        auth.hasGate("admin") ||
        auth.hasGate("examOfficeEmployee") ||
        data.mayManageHandicap === true
    const regularDateHasPassed = Boolean(
        exam?.fixedDate && !moment().isBefore(moment(exam.fixedDate))
    )

    useEffect(() => {
        if (!examId) {
            setLoadError(true)
            setLoading(false)
            return
        }

        Promise.all([
            axios.get(`/api/exam/exam/${examId}`, auth.axiosAuthHeader()),
            axios.get(`/api/exam/legitimization/${examId}`, auth.axiosAuthHeader()),
        ])
            .then(([examResponse, participantResponse]) => {
                setExam(examResponse.data)
                setData(participantResponse.data ?? emptyParticipantData)
                setLoadError(false)
            })
            .catch(error => {
                console.error("[Participant view] Failed to load participant data", error)
                setExam(null)
                setData(emptyParticipantData)
                setLoadError(true)
            })
            .finally(() => setLoading(false))
    }, [examId])

    const caretDefinition = (order: string | undefined) => {
        if (!order) return <i className="bi bi-arrow-down-up ms-1"/>
        return order === "asc"
            ? <i className="bi bi-sort-up ms-1"/>
            : <i className="bi bi-sort-down ms-1"/>
    }

    const toggleExpand = (id: string) => {
        setExpandedRow(previous => previous === id ? null : id)
    }

    const openHandicap = (entry: ParticipantEntry) => {
        setHandicapEntry(entry)
        setHandicapForm({
            handicap: Number(entry.legitimization?.handicap ?? 0),
            exclusiveBooking: entry.legitimization?.exclusiveBooking === true,
            handicapNotes: entry.legitimization?.handicapNotes ?? "",
            notifyParticipant: false,
        })
        setHandicapError(null)
    }

    const saveHandicap = async () => {
        if (!examId || !handicapEntry?.legitimization) return
        setSavingHandicap(true)
        setHandicapError(null)

        try {
            const response = await axios.put(
                `/api/exam/legitimization/${examId}/${handicapEntry.legitimization._id}/handicap`,
                handicapForm,
                auth.axiosAuthHeader()
            )

            setData(previous => {
                const entries = previous.entries.map(entry =>
                    entry.id === handicapEntry.id
                        ? {...entry, legitimization: response.data}
                        : entry
                )
                return {
                    ...previous,
                    entries,
                    handicapCount: entries.filter(entry =>
                        Number(entry.legitimization?.handicap ?? 0) > 0
                    ).length,
                }
            })
            setHandicapEntry(null)
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                const responseData = error.response?.data
                const responseMessage = typeof responseData === "string"
                    ? responseData
                    : responseData?.message ?? responseData?.msg
                const status = error.response?.status

                setHandicapError(
                    responseMessage ||
                    (status
                        ? t("participants.handicap.saveHttpError", {status})
                        : t("participants.handicap.backendError", {message: error.message}))
                )
            } else {
                setHandicapError(t("participants.handicap.saveError"))
            }
        } finally {
            setSavingHandicap(false)
        }
    }

    const saveManualParticipants = async () => {
        if (!examId || !manualIdentifiers.trim()) return
        setSavingManualParticipants(true)
        setManualParticipantsError(null)
        setManualParticipantsResult(null)

        try {
            const response = await axios.post(
                `/api/exam/exam/${examId}/legitimizations/manual`,
                {
                    identifiers: manualIdentifiers,
                    notifyParticipants: manualNotifyParticipants,
                },
                auth.axiosAuthHeader()
            )
            const created = response.data?.created?.length ?? 0
            const existing = response.data?.existing?.length ?? 0
            const invalid = response.data?.invalid?.length ?? 0
            const notificationsQueued = response.data?.notificationsQueued ?? 0
            const notificationsUnavailable = response.data?.notificationsUnavailable ?? 0
            setManualParticipantsResult(manualNotifyParticipants
                ? t("participants.manual.resultWithNotification", {
                    created,
                    existing,
                    invalid,
                    notificationsQueued,
                    notificationsUnavailable,
                })
                : t("participants.manual.result", {created, existing, invalid}))
            setManualIdentifiers("")
            setManualNotifyParticipants(false)

            const participantResponse = await axios.get(
                `/api/exam/legitimization/${examId}`,
                auth.axiosAuthHeader()
            )
            setData(participantResponse.data ?? emptyParticipantData)
        } catch (error: unknown) {
            setManualParticipantsError(axios.isAxiosError(error)
                ? error.response?.data?.message ?? t("participants.manual.saveError")
                : t("participants.manual.saveError"))
        } finally {
            setSavingManualParticipants(false)
        }
    }

    const requestManualParticipantDeletion = (entries: ParticipantEntry[]) => {
        const manualEntries = entries.filter(entry =>
            entry.legitimization?.fromTucan === false
        )
        if (manualEntries.length) setPendingManualDeletion(manualEntries)
    }

    const deleteManualParticipants = async (notifyParticipants: boolean) => {
        if (!examId) return
        const manualEntries = pendingManualDeletion
        if (!manualEntries.length) return

        setDeletingManualParticipants(true)
        setParticipantActionError(null)
        setParticipantActionSuccess(null)

        try {
            const response = await axios.post(
                `/api/exam/exam/${examId}/legitimizations/manual/delete`,
                {
                    legitimizationIds: manualEntries.map(entry => entry.legitimization!._id),
                    notifyParticipants,
                },
                auth.axiosAuthHeader()
            )
            const deleted = response.data?.deleted?.length ?? 0
            const blocked = response.data?.blocked?.length ?? 0
            const skipped = response.data?.skipped?.length ?? 0
            const notificationsQueued = response.data?.notificationsQueued ?? 0
            const notificationsUnavailable = response.data?.notificationsUnavailable ?? 0
            const notificationResult = notifyParticipants
                ? ` ${t("participants.manual.notificationResult", {
                    notificationsQueued,
                    notificationsUnavailable,
                })}`
                : ""

            if (blocked || skipped) {
                setParticipantActionError(t("participants.manual.deletePartial", {
                    deleted, blocked, skipped,
                }) + notificationResult)
            } else {
                setParticipantActionSuccess(
                    t("participants.manual.deleteSuccess", {count: deleted}) + notificationResult
                )
            }

            setSelectedRows([])
            setPendingManualDeletion([])
            const participantResponse = await axios.get(
                `/api/exam/legitimization/${examId}`,
                auth.axiosAuthHeader()
            )
            setData(participantResponse.data ?? emptyParticipantData)
        } catch (error: unknown) {
            setParticipantActionError(axios.isAxiosError(error)
                ? error.response?.data?.message ?? t("participants.manual.deleteError")
                : t("participants.manual.deleteError"))
        } finally {
            setDeletingManualParticipants(false)
        }
    }

    const saveFixedDateAttendance = async (attended: boolean) => {
        if (!examId || !fixedDateIdentifiers.trim()) return
        setSavingFixedDateAttendance(true)
        setFixedDateAttendanceError(null)
        setFixedDateAttendanceResult(null)

        try {
            const response = await axios.put(
                `/api/exam/exam/${examId}/legitimizations/fixed-date-attendance`,
                {identifiers: fixedDateIdentifiers, attended},
                auth.axiosAuthHeader()
            )
            setFixedDateAttendanceResult(t("participants.fixedDate.result", {
                updated: response.data?.updated?.length ?? 0,
                unchanged: response.data?.unchanged?.length ?? 0,
                notFound: response.data?.notFound?.length ?? 0,
                invalid: response.data?.invalid?.length ?? 0,
            }))
            setFixedDateIdentifiers("")

            const participantResponse = await axios.get(
                `/api/exam/legitimization/${examId}`,
                auth.axiosAuthHeader()
            )
            setData(participantResponse.data ?? emptyParticipantData)
        } catch (error: unknown) {
            setFixedDateAttendanceError(axios.isAxiosError(error)
                ? error.response?.data?.message ?? t("participants.fixedDate.saveError")
                : t("participants.fixedDate.saveError"))
        } finally {
            setSavingFixedDateAttendance(false)
        }
    }

    const expandRow = {
        renderer: (row: ParticipantEntry) => (
            <div>
                {row.legitimization ? (
                    <p className="mb-0"><Trans ns="examManagement" i18nKey="participants.bookingEnabled"
                        values={{date: moment(row.legitimization.createdAt).format("DD.MM.YYYY HH:mm")}}
                        components={{strong: <strong/>}}/></p>
                ) : (
                    <p className="mb-0">
                        {t("participants.demoWithoutLegitimization")}
                    </p>
                )}

                {!row.booking && (
                    <p className="mb-0 mt-2">
                        {t("participants.noBookingForPerson")}
                    </p>
                )}
            </div>
        ),
        expanded: expandedRow ? [expandedRow] : [],
        expandByColumnOnly: true,
    }

    const columns = [
        {
            dataField: "id",
            text: "#",
            formatter: (_cell: unknown, _row: ParticipantEntry, rowIndex: number) => rowIndex + 1,
            sort: false,
            filter: false,
        },
        {
            dataField: "esi",
            text: "ESI",
            sort: true,
            filter: textFilter({
                placeholder: " ",
            }),
            formatter: (
                _cell: unknown,
                row: ParticipantEntry
            ) =>
                formatEsi(
                    row.legitimization?.esi ??
                    row.user?.esi
                ),
            filterValue: (
                _cell: unknown,
                row: ParticipantEntry
            ) =>
                formatEsi(
                    row.legitimization?.esi ??
                    row.user?.esi
                ),
            sortValue: (
                _cell: unknown,
                row: ParticipantEntry
            ) =>
                formatEsi(
                    row.legitimization?.esi ??
                    row.user?.esi
                ),
            sortCaret: caretDefinition,
        },
        {
            dataField: "user.given_name",
            text: t("access.columns.givenName"),
            sort: true,
            filter: textFilter({placeholder: " "}),
            formatter: (_cell: string | undefined, row: ParticipantEntry) =>
                row.user?.given_name || row.legitimization?.firstName || "–",
            filterValue: (_cell: string | undefined, row: ParticipantEntry) =>
                row.user?.given_name || row.legitimization?.firstName || "",
            sortValue: (_cell: string | undefined, row: ParticipantEntry) =>
                row.user?.given_name || row.legitimization?.firstName || "",
            sortCaret: caretDefinition,
        },
        {
            dataField: "user.family_name",
            text: t("access.columns.familyName"),
            sort: true,
            filter: textFilter({placeholder: " "}),
            formatter: (_cell: string | undefined, row: ParticipantEntry) =>
                row.user?.family_name || row.legitimization?.lastName || "–",
            filterValue: (_cell: string | undefined, row: ParticipantEntry) =>
                row.user?.family_name || row.legitimization?.lastName || "",
            sortValue: (_cell: string | undefined, row: ParticipantEntry) =>
                row.user?.family_name || row.legitimization?.lastName || "",
            sortCaret: caretDefinition,
        },
        {
            dataField: "user.email",
            text: t("participants.columns.email"),
            sort: true,
            filter: textFilter({placeholder: " "}),
            formatter: (_cell: string | undefined, row: ParticipantEntry) =>
                row.user?.email || row.legitimization?.email || "–",
            filterValue: (_cell: string | undefined, row: ParticipantEntry) =>
                row.user?.email || row.legitimization?.email || "",
            sortValue: (_cell: string | undefined, row: ParticipantEntry) =>
                row.user?.email || row.legitimization?.email || "",
            sortCaret: caretDefinition,
        },
        {
            dataField: "booking.bookingSlot.entryAt",
            text: t("participants.columns.bookingAt"),
            sort: true,
            filter: textFilter({placeholder: " "}),
            formatter: (cell: string | undefined) =>
                cell ? moment(cell).format("dd, DD.MM.YYYY HH:mm") : "–",
            filterValue: (cell: string | undefined) =>
                cell ? moment(cell).format("dd, DD.MM.YYYY HH:mm") : t("participants.status.notBooked"),
            sortCaret: caretDefinition,
        },
        {
            dataField: "booking.state",
            text: t("overview.columns.status"),
            sort: true,
            filter: selectFilter({
                options: {
                    0: t("participants.status.booked"),
                    1: t("participants.status.attended"),
                    2: t("participants.status.missed"),
                    3: t("participants.status.notBooked"),
                },
                placeholder: " ",
            }),
            formatter: (_cell: unknown, row: ParticipantEntry) => (
                <span
                    style={{cursor: "pointer"}}
                    onClick={event => {
                        event.stopPropagation()
                        toggleExpand(row.id)
                    }}
                >
                    {getParticipantStatusLabel(row, t)}
                </span>
            ),
            filterValue: (_cell: unknown, row: ParticipantEntry) =>
                getParticipantStatusFilterValue(row),
            sortValue: (_cell: unknown, row: ParticipantEntry) =>
                getParticipantStatusLabel(row, t),
            sortCaret: caretDefinition,
        },
        {
            dataField: "features",
            text: t("participants.columns.features"),
            filter: selectFilter({
                options: {
                    demoCompleted: t("participants.features.demoCompleted"),
                    demoMissed: t("participants.features.demoMissed"),
                    demoBooked: t("participants.features.demoBooked"),
                    handicapExtension: t("participants.features.handicapExtension"),
                    exclusiveBooking: t("participants.features.exclusiveBooking"),
                    handicapNotes: t("participants.features.handicapNotes"),
                    neverLoggedIn: t("participants.features.neverLoggedIn"),
                    attendedFixedDate: t("participants.features.attendedFixedDate"),
                },
                comparator: Comparator.LIKE,
                placeholder: t("participants.features.placeholder"),
            }),
            filterValue: (_cell: unknown, row: ParticipantEntry) => {
                const features: string[] = []
                const demoSlot = row.demo?.bookingSlot

                if (row.demo && demoSlot) {
                    features.push(
                        row.demo.state === "QUIT"
                            ? "demoCompleted"
                            : moment().isAfter(moment(demoSlot.entryAt))
                                ? "demoMissed"
                                : "demoBooked"
                    )
                }
                if (Number(row.legitimization?.handicap ?? 0) > 0) {
                    features.push("handicapExtension")
                }
                if (row.legitimization?.exclusiveBooking) {
                    features.push("exclusiveBooking")
                }
                if (row.legitimization?.handicapNotes?.trim()) {
                    features.push("handicapNotes")
                }
                if (!row.user) {
                    features.push("neverLoggedIn")
                }
                if (row.legitimization?.attendedFixedDate) {
                    features.push("attendedFixedDate")
                }

                return features.join(" ")
            },
            formatter: (_cell: unknown, row: ParticipantEntry) => {
                const demoSlot = row.demo?.bookingSlot ?? null

                return (
                    <>
                        {row.demo && demoSlot && (
                            <OverlayTrigger
                                placement="left"
                                overlay={
                                    <Tooltip id={`demo-${row.id}`}>
                                        {row.demo.state === "QUIT"
                                            ? t("participants.demo.completed", {date: moment(demoSlot.entryAt).format("LL")})
                                            : moment().isAfter(moment(demoSlot.entryAt))
                                                ? t("participants.demo.missed", {date: moment(demoSlot.entryAt).format("LL")})
                                                : t("participants.demo.booked", {date: moment(demoSlot.entryAt).format("LL")})}
                                    </Tooltip>
                                }
                            >
                                <i
                                    className={
                                        row.demo.state === "QUIT"
                                            ? "bi bi-rocket-takeoff text-success me-1"
                                            : moment().isAfter(moment(demoSlot.entryAt))
                                                ? "bi bi-rocket-takeoff text-danger me-1"
                                                : "bi bi-rocket-takeoff text-secondary me-1"
                                    }
                                />
                            </OverlayTrigger>
                        )}

                        {Number(
                            row.legitimization?.handicap ?? 0
                        ) > 0 && (
                            <OverlayTrigger
                                placement="left"
                                overlay={
                                    <Tooltip id={`handicap-${row.id}`}>
                                        {t("participants.handicap.extension", {count: row.legitimization?.handicap ?? 0})}
                                    </Tooltip>
                                }
                            >
                                <i className="bi bi-clock-history me-1"/>
                            </OverlayTrigger>
                        )}

                        {row.legitimization?.exclusiveBooking && (
                            <OverlayTrigger
                                placement="left"
                                overlay={
                                    <Tooltip id={`exclusive-${row.id}`}>
                                        {t("participants.handicap.exclusive")}
                                    </Tooltip>
                                }
                            >
                                <i className="bi bi-lock"/>
                            </OverlayTrigger>
                        )}

                        {row.legitimization?.handicapNotes && (
                            <OverlayTrigger
                                placement="left"
                                overlay={<Tooltip id={`handicap-notes-${row.id}`}>
                                    {row.legitimization.handicapNotes}
                                </Tooltip>}
                            >
                                <i className="bi bi-card-text me-1"/>
                            </OverlayTrigger>
                        )}

                        {!row.user && (
                            <OverlayTrigger
                                placement="left"
                                overlay={<Tooltip id={`never-logged-in-${row.id}`}>
                                    {t("participants.features.neverLoggedIn")}
                                </Tooltip>}
                            >
                                <i className="bi bi-person-x-fill text-warning me-1"/>
                            </OverlayTrigger>
                        )}

                        {row.legitimization?.attendedFixedDate && (
                            <OverlayTrigger
                                placement="left"
                                overlay={<Tooltip id={`fixed-date-${row.id}`}>
                                    {t("participants.features.attendedFixedDate")}
                                </Tooltip>}
                            >
                                <i className="bi bi-calendar-check-fill text-success me-1"/>
                            </OverlayTrigger>
                        )}

                    </>
                )
            },
        },
        {
            dataField: "actions",
            text: t("overview.columns.actions"),
            sort: false,
            filter: false,
            formatter: (_cell: unknown, row: ParticipantEntry) => {
                if (!row.legitimization) {
                    return <span className="text-muted">–</span>
                }

                const hasHandicap =
                    Number(row.legitimization.handicap ?? 0) > 0 ||
                    row.legitimization.exclusiveBooking === true ||
                    Boolean(row.legitimization.handicapNotes?.trim())

                const isManual = row.legitimization.fromTucan === false

                return <div className="d-flex gap-1">
                    {mayManageHandicap && <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip id={`manage-handicap-${row.id}`}>
                                {t(hasHandicap ? "participants.handicap.edit" : "participants.handicap.create")}
                            </Tooltip>
                        }
                    >
                        <Button
                            size="sm"
                            variant={hasHandicap ? "danger" : "secondary"}
                            onClick={event => {
                                event.stopPropagation()
                                openHandicap(row)
                            }}
                        >
                            <i className="bi bi-universal-access"/>
                        </Button>
                    </OverlayTrigger>}
                    {data.mayManageParticipants && isManual && <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id={`delete-manual-${row.id}`}>
                            {t("participants.manual.delete")}
                        </Tooltip>}
                    >
                        <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={event => {
                                event.stopPropagation()
                                requestManualParticipantDeletion([row])
                            }}
                        >
                            <i className="bi bi-trash"/>
                        </Button>
                    </OverlayTrigger>}
                    {data.mayManageParticipants && !isManual && <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id={`tucan-managed-${row.id}`}>
                            {t("participants.manual.tucanManaged")}
                        </Tooltip>}
                    >
                        <span className="text-muted px-2 py-1"><i className="bi bi-lock-fill"/></span>
                    </OverlayTrigger>}
                </div>
            },
        },
    ]

    const handleSelectedRows = () => {
        const emailAddresses = Array.from(
            new Set(
                selectedRows
                    .map(entry => entry.user?.email ?? entry.legitimization?.email)
                    .filter((email): email is string => Boolean(email))
            )
        )

        if (!emailAddresses.length) return

        const mailParams = emailAddresses.length > 1
            ? new URLSearchParams({
                bcc: emailAddresses.join(","),
                subject: "👋",
                body: "",
            })
            : new URLSearchParams({
                to: emailAddresses[0],
                subject: "👋",
                body: "",
            })

        window.location.href = `mailto:?${mailParams.toString()}`
    }

    return (
        <div className="component">
            <Container fluid>
                <Row>
                    <Col>
                        <Card>
                            <Card.Header>
                                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                                    <div>
                                        <h3 className="mb-0">
                                            <i className="bi bi-people-fill me-2"/>
                                            {t("participants.title")}
                                        </h3>
                                        {exam && (
                                            <div className="text-muted mt-1">
                                                {i18n.language === "en" ? exam.name_en || exam.name_de : exam.name_de}
                                            </div>
                                        )}
                                    </div>

                                    <ButtonGroup>
                                        {data.mayManageParticipants && selectedRows.some(entry =>
                                            entry.legitimization?.fromTucan === false
                                        ) && (
                                            <Button
                                                variant="outline-danger"
                                                onClick={() => requestManualParticipantDeletion(selectedRows)}
                                            >
                                                <i className="bi bi-trash me-1"/>
                                                {t("participants.manual.deleteSelected")}
                                            </Button>
                                        )}
                                        {data.mayManageParticipants && (
                                            <Button
                                                variant="outline-success"
                                                disabled={!regularDateHasPassed}
                                                title={!regularDateHasPassed
                                                    ? t("participants.fixedDate.notOccurredYet")
                                                    : undefined}
                                                onClick={() => {
                                                    setFixedDateAttendanceError(null)
                                                    setFixedDateAttendanceResult(null)
                                                    setShowFixedDateAttendance(true)
                                                }}
                                            >
                                                <i className="bi bi-calendar-check me-1"/>
                                                {t("participants.fixedDate.action")}
                                            </Button>
                                        )}
                                        {data.mayManageParticipants && (
                                            <Button
                                                variant="success"
                                                onClick={() => {
                                                    setManualParticipantsError(null)
                                                    setManualParticipantsResult(null)
                                                    setManualNotifyParticipants(false)
                                                    setShowManualParticipants(true)
                                                }}
                                            >
                                                <i className="bi bi-person-plus-fill me-1"/>
                                                {t("participants.manual.action")}
                                            </Button>
                                        )}
                                        <Button
                                            disabled={loading || data.bookingCount === 0}
                                            onClick={() => nav(`/bookingDistribution/${examId}`)}
                                        >
                                            <i className="bi bi-bar-chart-steps me-1"/>
                                            {data.bookingCount > 0
                                                ? t("distribution.shortTitle")
                                                : t("distribution.unavailable")}
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            onClick={() => nav("/exam-management")}
                                        >
                                            <i className="bi bi-arrow-left me-1"/>
                                            {t("backToOverview")}
                                        </Button>
                                    </ButtonGroup>
                                </div>
                            </Card.Header>

                            <Card.Body>
                                {participantActionError && <Alert variant="warning">{participantActionError}</Alert>}
                                {participantActionSuccess && <Alert variant="success">{participantActionSuccess}</Alert>}
                                {loading && (
                                    <Alert className="text-center" variant="info">
                                        {t("participants.loading")}
                                    </Alert>
                                )}

                                {!loading && loadError && (
                                    <Alert className="text-center" variant="danger">
                                        {t("participants.loadError")}
                                    </Alert>
                                )}

                                {!loading && !loadError && exam && (
                                    <>
                                        <Row className="g-3 mb-4">
                                            <Col xs={12} md={6} xl={3}>
                                                <Card><Card.Body>
                                                    <div className="text-muted">
                                                        {data.isDemo
                                                            ? t("participants.metrics.demoBookings")
                                                            : t("participants.metrics.registrations")}
                                                    </div>

                                                    <div className="fs-3 fw-bold">
                                                        {data.isDemo
                                                            ? data.bookingCount
                                                            : data.legitimizationCount}
                                                    </div>
                                                    {!data.isDemo && (
                                                        <div className="small text-muted">
                                                            {t("participants.metrics.manuallyAdded", {
                                                                count: data.manualLegitimizationCount ?? 0,
                                                            })}
                                                            <br/>
                                                            {t("participants.metrics.fixedDateAttended", {
                                                                count: data.fixedDateAttendedCount ?? 0,
                                                            })}
                                                        </div>
                                                    )}
                                                </Card.Body></Card>
                                            </Col>
                                            <Col xs={12} md={6} xl={3}>
                                                <Card><Card.Body>
                                                    <div className="text-muted">{t("participants.metrics.bookings")}</div>
                                                    <div className="fs-3 fw-bold">{data.bookingCount}</div>
                                                </Card.Body></Card>
                                            </Col>
                                            <Col xs={12} md={6} xl={3}>
                                                <Card><Card.Body>
                                                    <div className="text-muted">{t("participants.status.attended")}</div>
                                                    <div className="fs-3 fw-bold">{data.attended}</div>
                                                </Card.Body></Card>
                                            </Col>
                                            <Col xs={12} md={6} xl={3}>
                                                <Card><Card.Body>
                                                    <div className="text-muted">{t("participants.metrics.missed")}</div>
                                                    <div className="fs-3 fw-bold">{data.notAttended}</div>
                                                </Card.Body></Card>
                                            </Col>
                                        </Row>

                                        <div className="mb-3">
                                            <strong>{t("overview.columns.bookingPeriod")}:</strong>{" "}
                                            {moment(exam.openBookingAt).format("DD.MM.YYYY HH:mm")} {t("timeSuffix")} {t("until")}{" "}
                                            {moment(exam.closeBookingAt).format("DD.MM.YYYY HH:mm")} {t("timeSuffix")}
                                            <br/>
                                            <strong>{t("overview.columns.examPeriod")}:</strong>{" "}
                                            {moment(exam.openExaminationRange).format("DD.MM.YYYY HH:mm")} {t("timeSuffix")} {t("until")}{" "}
                                            {moment(exam.closeExaminationRange).format("DD.MM.YYYY HH:mm")} {t("timeSuffix")}
                                        </div>

                                        {data.entries.length === 0 ? (
                                            <Alert className="text-center" variant="info">
                                                {data.isDemo
                                                    ? t("participants.emptyDemo")
                                                    : t("participants.emptyLegitimizations")}
                                            </Alert>
                                        ) : (
                                            <BootstrapTable
                                                keyField="id"
                                                data={data.entries}
                                                columns={columns}
                                                filter={filterFactory()}
                                                striped
                                                hover
                                                condensed
                                                expandRow={expandRow}
                                                defaultSorted={[{
                                                    dataField: "legitimization.esi",
                                                    order: "asc",
                                                }]}
                                                rowClasses={(row: ParticipantEntry) => {
                                                    const status = getParticipantStatus(row)
                                                    if (status === "ATTENDED") return "table-success"
                                                    if (status === "NOT_ATTENDED") return "table-danger"
                                                    return ""
                                                }}
                                                selectRow={{
                                                    mode: "checkbox",
                                                    classes: "justify-content-center",
                                                    onSelect: (row: ParticipantEntry, isSelect: boolean) => {
                                                        setSelectedRows(previous =>
                                                            isSelect
                                                                ? [...previous, row]
                                                                : previous.filter(entry => entry.id !== row.id)
                                                        )
                                                    },
                                                    onSelectAll: (isSelect: boolean, rows: ParticipantEntry[]) => {
                                                        setSelectedRows(isSelect ? rows : [])
                                                    },
                                                    selectionRenderer: ({checked}: any) => (
                                                        <div className="d-flex justify-content-center align-items-center">
                                                            <input type="checkbox" checked={checked} readOnly/>
                                                        </div>
                                                    ),
                                                    selectionHeaderRenderer: ({checked, indeterminate}: any) => (
                                                        <div className="d-flex flex-wrap align-items-center justify-content-center gap-1">
                                                            <OverlayTrigger
                                                                placement="right"
                                                                overlay={
                                                                    <Tooltip id="mail-tooltip">
                                                                        {t("participants.emailSelected")}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <Button
                                                                    size="sm"
                                                                    onClick={event => {
                                                                        event.stopPropagation()
                                                                        handleSelectedRows()
                                                                    }}
                                                                >
                                                                    <i className="bi bi-envelope-at"/>
                                                                </Button>
                                                            </OverlayTrigger>
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                ref={input => {
                                                                    if (input) input.indeterminate = indeterminate
                                                                }}
                                                                readOnly
                                                            />
                                                        </div>
                                                    ),
                                                }}
                                                noDataIndication={() => (
                                                    <Alert className="text-center" variant="info">
                                                        {t("noResults")}
                                                    </Alert>
                                                )}
                                            />
                                        )}
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <Modal
                show={showFixedDateAttendance}
                onHide={() => !savingFixedDateAttendance && setShowFixedDateAttendance(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t("participants.fixedDate.title")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{t("participants.fixedDate.description")}</p>
                    {fixedDateAttendanceError && <Alert variant="danger">{fixedDateAttendanceError}</Alert>}
                    {fixedDateAttendanceResult && <Alert variant="success">{fixedDateAttendanceResult}</Alert>}
                    <Form.Group>
                        <Form.Label>{t("participants.fixedDate.identifiers")}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={8}
                            value={fixedDateIdentifiers}
                            placeholder={t("participants.manual.placeholder")}
                            onChange={event => setFixedDateIdentifiers(event.target.value)}
                        />
                        <Form.Text>{t("participants.fixedDate.hint")}</Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        disabled={savingFixedDateAttendance}
                        onClick={() => setShowFixedDateAttendance(false)}
                    >
                        {t("cancel", {ns: "common"})}
                    </Button>
                    <Button
                        variant="outline-danger"
                        disabled={savingFixedDateAttendance || !fixedDateIdentifiers.trim()}
                        onClick={() => saveFixedDateAttendance(false)}
                    >
                        {t("participants.fixedDate.unmark")}
                    </Button>
                    <Button
                        variant="success"
                        disabled={
                            savingFixedDateAttendance ||
                            !fixedDateIdentifiers.trim() ||
                            !regularDateHasPassed
                        }
                        onClick={() => saveFixedDateAttendance(true)}
                    >
                        {savingFixedDateAttendance && <Spinner animation="border" size="sm" className="me-2"/>}
                        {t("participants.fixedDate.mark")}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showManualParticipants}
                onHide={() => !savingManualParticipants && setShowManualParticipants(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t("participants.manual.title")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{t("participants.manual.description")}</p>
                    {manualParticipantsError && <Alert variant="danger">{manualParticipantsError}</Alert>}
                    {manualParticipantsResult && <Alert variant="success">{manualParticipantsResult}</Alert>}
                    <Form.Group>
                        <Form.Label>{t("participants.manual.identifiers")}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={8}
                            value={manualIdentifiers}
                            placeholder={t("participants.manual.placeholder")}
                            onChange={event => setManualIdentifiers(event.target.value)}
                        />
                        <Form.Text>{t("participants.manual.hint")}</Form.Text>
                    </Form.Group>
                    <Form.Check
                        className="mt-3"
                        type="checkbox"
                        id="notify-manual-participants"
                        checked={manualNotifyParticipants}
                        onChange={event => setManualNotifyParticipants(event.target.checked)}
                        label={t("participants.manual.notifyOnAdd")}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        disabled={savingManualParticipants}
                        onClick={() => setShowManualParticipants(false)}
                    >
                        {t("cancel", {ns: "common"})}
                    </Button>
                    <Button
                        variant="success"
                        disabled={savingManualParticipants || !manualIdentifiers.trim()}
                        onClick={saveManualParticipants}
                    >
                        {savingManualParticipants && <Spinner animation="border" size="sm" className="me-2"/>}
                        {t("participants.manual.add")}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={pendingManualDeletion.length > 0}
                onHide={() => !deletingManualParticipants && setPendingManualDeletion([])}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t("participants.manual.deleteDialogTitle")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-0">{t("participants.manual.deleteDialogDescription", {
                        count: pendingManualDeletion.length,
                    })}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        disabled={deletingManualParticipants}
                        onClick={() => setPendingManualDeletion([])}
                    >
                        {t("cancel", {ns: "common"})}
                    </Button>
                    <Button
                        variant="outline-danger"
                        disabled={deletingManualParticipants}
                        onClick={() => void deleteManualParticipants(false)}
                    >
                        {t("participants.manual.deleteWithoutNotification")}
                    </Button>
                    <Button
                        variant="danger"
                        disabled={deletingManualParticipants}
                        onClick={() => void deleteManualParticipants(true)}
                    >
                        {deletingManualParticipants && <Spinner animation="border" size="sm" className="me-2"/>}
                        {t("participants.manual.deleteWithNotification")}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={Boolean(handicapEntry)}
                onHide={() => !savingHandicap && setHandicapEntry(null)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t("participants.handicap.title")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {handicapError && <Alert variant="danger">{handicapError}</Alert>}
                    <p className="text-muted">
                        {handicapEntry?.user
                            ? `${handicapEntry.user.given_name ?? ""} ${handicapEntry.user.family_name ?? ""}`.trim()
                            : formatEsi(handicapEntry?.legitimization?.esi)}
                    </p>
                    {handicapEntry?.legitimization?.handicapSetAt && (
                        <Alert variant="info">
                            {t("participants.handicap.lastSet", {date: moment(
                                handicapEntry.legitimization.handicapSetAt
                            ).format("DD.MM.YYYY HH:mm")})}
                            {typeof handicapEntry.legitimization.handicapSetBy === "object" && (
                                <> {t("participants.handicap.by")} {[
                                    handicapEntry.legitimization.handicapSetBy.given_name,
                                    handicapEntry.legitimization.handicapSetBy.family_name,
                                ].filter(Boolean).join(" ")}</>
                            )}.
                        </Alert>
                    )}
                    <Form.Group className="mb-3">
                        <Form.Label>{t("participants.handicap.minutes")}</Form.Label>
                        <Form.Control
                            type="number"
                            min={0}
                            max={600}
                            step={1}
                            value={handicapForm.handicap}
                            onChange={event => setHandicapForm(previous => ({
                                ...previous,
                                handicap: Number(event.target.value),
                            }))}
                        />
                    </Form.Group>
                    <Form.Check
                        className="mb-3"
                        type="switch"
                        label={t("participants.handicap.exclusiveLabel")}
                        checked={handicapForm.exclusiveBooking}
                        onChange={event => setHandicapForm(previous => ({
                            ...previous,
                            exclusiveBooking: event.target.checked,
                        }))}
                    />
                    <Form.Group className="mb-3">
                        <Form.Label>{t("participants.handicap.notes")}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={5}
                            maxLength={5000}
                            value={handicapForm.handicapNotes}
                            onChange={event => setHandicapForm(previous => ({
                                ...previous,
                                handicapNotes: event.target.value,
                            }))}
                        />
                        <Form.Text>{t("participants.handicap.characters", {count: handicapForm.handicapNotes.length})}</Form.Text>
                    </Form.Group>
                    <Form.Check
                        type="checkbox"
                        label={t("participants.handicap.notify")}
                        checked={handicapForm.notifyParticipant}
                        onChange={event => setHandicapForm(previous => ({
                            ...previous,
                            notifyParticipant: event.target.checked,
                        }))}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" disabled={savingHandicap}
                        onClick={() => setHandicapEntry(null)}>
                        {t("cancel", {ns: "common"})}
                    </Button>
                    <Button disabled={savingHandicap} onClick={saveHandicap}>
                        {savingHandicap && <Spinner animation="border" size="sm" className="me-2"/>}
                        {t("save", {ns: "common"})}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}

export default ParticipantView
